import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBottomSheet } from '@/hooks/useBottomSheet';

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'agent';
  timestamp: Date;
  agentName?: string;
}

interface TourStep {
  selector: string;
  title: string;
  description: string;
  action?: () => void;
  position: 'top' | 'bottom' | 'left' | 'right';
}

interface TourState {
  active: boolean;
  type: 'pix' | 'pagamento' | null;
  currentStep: number;
  steps: TourStep[];
}

export function ChatModal({ isOpen, onClose }: ChatModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Olá! 👋 Sou seu assistente virtual da plataforma de incentivos. Como posso ajudar você hoje?',
      sender: 'agent',
      timestamp: new Date(),
      agentName: 'Assistente Virtual'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Estado do Tour Virtual
  const [tour, setTour] = useState<TourState>({
    active: false,
    type: null,
    currentStep: 0,
    steps: []
  });
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Atualizar posição do highlight quando o tour está ativo
  useEffect(() => {
    if (tour.active && tour.steps[tour.currentStep]) {
      const updateHighlight = () => {
        const element = document.querySelector(tour.steps[tour.currentStep].selector);
        if (element) {
          const rect = element.getBoundingClientRect();
          setHighlightRect(rect);
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };
      
      // Pequeno delay para garantir que elementos estejam renderizados
      const timer = setTimeout(updateHighlight, 300);
      window.addEventListener('resize', updateHighlight);
      window.addEventListener('scroll', updateHighlight);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateHighlight);
        window.removeEventListener('scroll', updateHighlight);
      };
    }
  }, [tour.active, tour.currentStep, tour.steps]);

  const { mounted, sheetClass, backdropClass, handleClose: animatedClose } = useBottomSheet({ isOpen, onClose });

  // Não renderiza nada se não estiver aberto
  if (!mounted && !tour.active) return null;

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages([...messages, newMessage]);
    setInputMessage('');
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      const responses = [
        'Entendo sua questão. Deixe-me verificar isso para você.',
        'Obrigado pela informação. Vou analisar e retornar em instantes.',
        'Perfeito! Estou processando sua solicitação.',
        'Compreendo. Vou encaminhar isso para o setor responsável.',
        'Ótima pergunta! Vou buscar essas informações para você.'
      ];
      const agentResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: responses[Math.floor(Math.random() * responses.length)],
        sender: 'agent',
        timestamp: new Date(),
        agentName: 'Assistente Virtual'
      };
      setMessages(prev => [...prev, agentResponse]);
    }, 1500);
  };

  const quickQuestions = [
    { icon: 'ri-coins-line', text: 'Como ganhar pontos?', color: 'orange' },
    { icon: 'ri-gift-line', text: 'Como resgatar recompensas?', color: 'orange' },
    { icon: 'ri-medal-line', text: 'O que são distintivos?', color: 'orange' },
    { icon: 'ri-checkbox-circle-line', text: 'Como completar missões?', color: 'orange' },
    { icon: 'ri-bar-chart-line', text: 'Como subir de nível?', color: 'orange' },
    { icon: 'ri-gamepad-line', text: 'Quais jogos disponíveis?', color: 'orange' }
  ];

  const guidedRedemptions = [
    { icon: 'ri-exchange-dollar-line', text: 'Resgate PIX', color: 'teal', tourType: 'pix' as const },
    { icon: 'ri-bill-line', text: 'Pagamento de Contas', color: 'teal', tourType: 'pagamento' as const }
  ];

  const handleQuickQuestion = (question: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text: question,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages([...messages, newMessage]);
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      let response = '';
      
      if (question.includes('ganhar pontos')) {
        response = 'Você pode ganhar pontos de várias formas: completando vendas, atingindo metas, participando de campanhas, completando missões diárias e jogando os jogos disponíveis na plataforma!';
      } else if (question.includes('resgatar recompensas')) {
        response = 'Para resgatar recompensas, acesse o menu "Recompensas" e escolha entre as opções disponíveis: PIX, Pagamento de Contas, Produtos, Vouchers, Experiências ou Cursos. Você pode usar seus pontos acumulados para qualquer resgate!';
      } else if (question.includes('distintivos')) {
        response = 'Distintivos são conquistas especiais que você ganha ao completar desafios e marcos na plataforma. Acesse "Distintivos" no menu para ver todos disponíveis e seu progresso!';
      } else if (question.includes('missões')) {
        response = 'Missões são desafios diários e semanais que você pode completar para ganhar pontos extras e XP. Acesse "Missões" no menu para ver as disponíveis hoje!';
      } else if (question.includes('subir de nível')) {
        response = 'Você sobe de nível ganhando XP (experiência) através de vendas, missões completadas, distintivos conquistados e participação em campanhas. Cada nível desbloqueado traz novos benefícios!';
      } else if (question.includes('jogos')) {
        response = 'Temos diversos jogos disponíveis: Roleta da Sorte, Raspadinha, Caça ao Tesouro e muito mais! Acesse "Jogos" no menu para jogar e ganhar prêmios extras!';
      }

      const agentResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: response,
        sender: 'agent',
        timestamp: new Date(),
        agentName: 'Assistente Virtual'
      };
      setMessages(prev => [...prev, agentResponse]);
    }, 1500);
  };

  // Iniciar Tour Virtual
  const startTour = (tourType: 'pix' | 'pagamento') => {
    const isOnRecompensasPage = location.pathname.includes('/premiado/recompensas');
    
    const pixSteps: TourStep[] = [
      ...(isOnRecompensasPage ? [] : [{
        selector: '[data-tour="menu-recompensas"], a[href*="recompensas"]',
        title: '1️⃣ Acesse Recompensas',
        description: 'Primeiro, clique no menu "Recompensas" na barra lateral',
        position: 'right' as const,
        action: () => navigate('/premiado/recompensas')
      }]),
      {
        selector: '[data-tour="aba-financeira"]',
        title: isOnRecompensasPage ? '1️⃣ Categoria Financeira' : '2️⃣ Categoria Financeira',
        description: 'Clique na aba "Financeira" para ver as opções de resgate em dinheiro',
        position: 'bottom' as const
      },
      {
        selector: '[data-tour="card-pix"]',
        title: isOnRecompensasPage ? '2️⃣ Selecione PIX' : '3️⃣ Selecione PIX',
        description: 'Clique no card "PIX" para iniciar o resgate',
        position: 'right' as const
      },
      {
        selector: '[data-tour="btn-resgatar-pix"]',
        title: isOnRecompensasPage ? '3️⃣ Clique em Resgatar' : '4️⃣ Clique em Resgatar',
        description: 'Clique no botão "Resgatar Agora" para abrir o formulário',
        position: 'top' as const
      },
      // Passos do Formulário PIX
      {
        selector: '[data-tour="pix-credito-select"]',
        title: '4️⃣ Selecione o Crédito',
        description: 'Escolha qual crédito você deseja resgatar. Cada crédito tem um valor específico de uma campanha.',
        position: 'bottom' as const
      },
      {
        selector: '[data-tour="pix-tipo-chave"]',
        title: '5️⃣ Tipo de Chave PIX',
        description: 'Selecione o tipo da sua chave PIX: CPF/CNPJ, E-mail, Celular ou Chave Aleatória.',
        position: 'bottom' as const
      },
      {
        selector: '[data-tour="pix-chave-input"]',
        title: '6️⃣ Digite sua Chave PIX',
        description: 'Informe a chave PIX para onde deseja receber o valor. Confira se está correta!',
        position: 'bottom' as const
      },
      {
        selector: '[data-tour="pix-descricao"]',
        title: '7️⃣ Descrição (Opcional)',
        description: 'Se quiser, adicione uma descrição para identificar esta transferência.',
        position: 'top' as const
      },
      {
        selector: '[data-tour="pix-btn-continuar"]',
        title: '8️⃣ Continuar',
        description: 'Clique em "Continuar" para revisar os dados antes de confirmar.',
        position: 'top' as const
      },
      {
        selector: '[data-tour="pix-btn-confirmar"]',
        title: '9️⃣ Confirme a Transferência',
        description: 'Revise todos os dados e clique em "Confirmar PIX" para finalizar o resgate!',
        position: 'top' as const
      }
    ];

    const pagamentoSteps: TourStep[] = [
      ...(isOnRecompensasPage ? [] : [{
        selector: '[data-tour="menu-recompensas"], a[href*="recompensas"]',
        title: '1️⃣ Acesse Recompensas',
        description: 'Primeiro, clique no menu "Recompensas" na barra lateral',
        position: 'right' as const,
        action: () => navigate('/premiado/recompensas')
      }]),
      {
        selector: '[data-tour="aba-financeira"]',
        title: isOnRecompensasPage ? '1️⃣ Categoria Financeira' : '2️⃣ Categoria Financeira',
        description: 'Clique na aba "Financeira" para ver as opções de pagamento',
        position: 'bottom' as const
      },
      {
        selector: '[data-tour="card-pagamento"]',
        title: isOnRecompensasPage ? '2️⃣ Pagamento de Contas' : '3️⃣ Pagamento de Contas',
        description: 'Clique no card "Pagamento de Contas"',
        position: 'right' as const
      },
      {
        selector: '[data-tour="btn-resgatar-pagamento"]',
        title: isOnRecompensasPage ? '3️⃣ Clique em Resgatar' : '4️⃣ Clique em Resgatar',
        description: 'Clique no botão "Resgatar Agora" para abrir o formulário de pagamento',
        position: 'top' as const
      },
      // Passos do Formulário Pagamento de Contas
      {
        selector: '[data-tour="pagamento-credito-select"]',
        title: '4️⃣ Selecione o Crédito',
        description: 'Escolha qual crédito você deseja usar para pagar a conta. O valor do crédito deve cobrir o valor da conta.',
        position: 'bottom' as const
      },
      {
        selector: '[data-tour="pagamento-tipo"]',
        title: '5️⃣ Tipo de Pagamento',
        description: 'Selecione se é um Boleto bancário ou uma Conta de Consumo (luz, água, gás, etc).',
        position: 'bottom' as const
      },
      {
        selector: '[data-tour="pagamento-codigo"]',
        title: '6️⃣ Código de Barras',
        description: 'Digite ou cole o código de barras da conta. Você também pode usar o scanner de câmera.',
        position: 'bottom' as const
      },
      {
        selector: '[data-tour="pagamento-btn-validar"]',
        title: '7️⃣ Validar Código',
        description: 'Clique em "Validar Código" para verificar os dados da conta antes de pagar.',
        position: 'top' as const
      },
      {
        selector: '[data-tour="pagamento-btn-continuar"]',
        title: '8️⃣ Continuar',
        description: 'Após validar, clique em "Continuar" para revisar os dados do pagamento.',
        position: 'top' as const
      },
      {
        selector: '[data-tour="pagamento-btn-confirmar"]',
        title: '9️⃣ Confirme o Pagamento',
        description: 'Revise todos os dados e clique em "Confirmar Pagamento" para finalizar!',
        position: 'top' as const
      }
    ];

    const steps = tourType === 'pix' ? pixSteps : pagamentoSteps;

    // Fechar modal imediatamente
    onClose();
    
    // Função para verificar se o elemento existe e iniciar o tour
    const waitForElementAndStartTour = () => {
      const firstElement = document.querySelector(steps[0].selector);
      
      if (firstElement) {
        // Elemento encontrado, iniciar tour
        setTour({
          active: true,
          type: tourType,
          currentStep: 0,
          steps: steps
        });
      } else {
        // Elemento não encontrado, tentar novamente após um delay
        setTimeout(waitForElementAndStartTour, 200);
      }
    };
    
    // Iniciar tour após fechar o modal
    setTimeout(() => {
      // Se não estiver na página de recompensas, navegar primeiro
      if (!isOnRecompensasPage) {
        navigate('/premiado/recompensas');
        // Aguardar navegação e renderização da página
        setTimeout(waitForElementAndStartTour, 800);
      } else {
        // Já está na página, aguardar apenas um pouco para garantir renderização
        setTimeout(waitForElementAndStartTour, 300);
      }
    }, 100);
  };

  // Avançar para próximo passo do tour
  const nextTourStep = () => {
    if (tour.currentStep < tour.steps.length - 1) {
      const currentStepData = tour.steps[tour.currentStep];
      if (currentStepData.action) {
        currentStepData.action();
      }
      setTour(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
    } else {
      // Finalizar tour
      endTour();
    }
  };

  // Voltar passo do tour
  const prevTourStep = () => {
    if (tour.currentStep > 0) {
      setTour(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
    }
  };

  // Finalizar tour
  const endTour = () => {
    setTour({
      active: false,
      type: null,
      currentStep: 0,
      steps: []
    });
    setHighlightRect(null);
  };

  // Renderizar overlay do tour
  const renderTourOverlay = () => {
    if (!tour.active) return null;

    const currentStepData = tour.steps[tour.currentStep];
    const isLastStep = tour.currentStep === tour.steps.length - 1;

    // Calcular posição do tooltip
    const getTooltipPosition = () => {
      if (!highlightRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
      
      const padding = 16;
      const tooltipWidth = 320;
      const tooltipHeight = 180;
      
      switch (currentStepData.position) {
        case 'top':
          return {
            top: `${highlightRect.top - tooltipHeight - padding}px`,
            left: `${highlightRect.left + highlightRect.width / 2 - tooltipWidth / 2}px`
          };
        case 'bottom':
          return {
            top: `${highlightRect.bottom + padding}px`,
            left: `${highlightRect.left + highlightRect.width / 2 - tooltipWidth / 2}px`
          };
        case 'left':
          return {
            top: `${highlightRect.top + highlightRect.height / 2 - tooltipHeight / 2}px`,
            left: `${highlightRect.left - tooltipWidth - padding}px`
          };
        case 'right':
          return {
            top: `${highlightRect.top + highlightRect.height / 2 - tooltipHeight / 2}px`,
            left: `${highlightRect.right + padding}px`
          };
        default:
          return {
            top: `${highlightRect.bottom + padding}px`,
            left: `${highlightRect.left}px`
          };
      }
    };

    const tooltipStyle = getTooltipPosition();

    return (
      <div className="fixed inset-0 z-[9999]">
        {/* Overlay escuro com buraco */}
        <div className="absolute inset-0 bg-black/70" />
        
        {/* Área destacada (buraco no overlay) */}
        {highlightRect && (
          <div
            className="absolute bg-transparent border-4 border-teal-400 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.7)] animate-pulse"
            style={{
              top: highlightRect.top - 8,
              left: highlightRect.left - 8,
              width: highlightRect.width + 16,
              height: highlightRect.height + 16,
              boxShadow: '0 0 0 4px rgba(20, 184, 166, 0.5), 0 0 30px rgba(20, 184, 166, 0.8)'
            }}
          />
        )}

        {/* Tooltip com instruções */}
        <div
          className="absolute bg-white rounded-2xl shadow-2xl p-5 w-80 animate-[fadeInScale_0.3s_ease-out]"
          style={tooltipStyle}
        >
          {/* Header do tooltip */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center">
                <i className="ri-cursor-line text-white text-sm"></i>
              </div>
              <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">
                Tour Guiado
              </span>
            </div>
            <button
              onClick={endTour}
              className="w-6 h-6 flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-slate-400"></i>
            </button>
          </div>

          {/* Título e descrição */}
          <h3 className="text-lg font-bold text-slate-900 mb-2">{currentStepData.title}</h3>
          <p className="text-sm text-slate-600 mb-4">{currentStepData.description}</p>

          {/* Indicador de progresso */}
          <div className="flex items-center gap-1 mb-4">
            {tour.steps.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  index <= tour.currentStep ? 'bg-teal-500' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>

          {/* Botões de navegação */}
          <div className="flex items-center justify-between">
            <button
              onClick={prevTourStep}
              disabled={tour.currentStep === 0}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                tour.currentStep === 0
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <i className="ri-arrow-left-line mr-1"></i>
              Anterior
            </button>
            
            <span className="text-xs text-slate-400">
              {tour.currentStep + 1} de {tour.steps.length}
            </span>

            <button
              onClick={nextTourStep}
              className="px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg text-sm font-semibold hover:from-teal-600 hover:to-teal-700 transition-all cursor-pointer whitespace-nowrap"
            >
              {isLastStep ? (
                <>
                  Concluir
                  <i className="ri-check-line ml-1"></i>
                </>
              ) : (
                <>
                  Próximo
                  <i className="ri-arrow-right-line ml-1"></i>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Seta apontando para o elemento */}
        {highlightRect && (
          <div
            className="absolute text-teal-400 animate-bounce"
            style={{
              top: highlightRect.top - 40,
              left: highlightRect.left + highlightRect.width / 2 - 12,
            }}
          >
            <i className="ri-arrow-down-line text-3xl drop-shadow-lg"></i>
          </div>
        )}
      </div>
    );
  };

  // Renderizar apenas o overlay do tour se o modal estiver fechado mas o tour ativo
  if (!isOpen && tour.active) {
    return renderTourOverlay();
  }

  if (!mounted) return null;

  return (
    <>
      <div className={`fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4 ${backdropClass}`} onClick={animatedClose}>
        <div className={`bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-2xl w-full flex flex-col ${sheetClass}`} style={{ maxHeight: 'calc(100dvh - 64px)', height: '600px' }} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-orange-600">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <i className="ri-robot-2-line text-orange-600 text-xl"></i>
                </div>
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Assistente Virtual</h2>
                <div className="text-xs text-orange-100">🤖 Online agora</div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center hover:bg-orange-700 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white text-xl"></i>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-end space-x-2 max-w-[70%] ${message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  {message.sender === 'agent' && (
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-robot-2-line text-white text-sm"></i>
                    </div>
                  )}
                  <div>
                    {message.sender === 'agent' && message.agentName && (
                      <div className="text-xs text-gray-500 mb-1 ml-2">{message.agentName}</div>
                    )}
                    <div
                      className={`px-4 py-2.5 rounded-2xl ${
                        message.sender === 'user'
                          ? 'bg-orange-500 text-white rounded-br-sm'
                          : 'bg-white text-gray-800 rounded-bl-sm shadow-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-line">{message.text}</p>
                    </div>
                    <div className={`text-xs text-gray-400 mt-1 ${message.sender === 'user' ? 'text-right mr-2' : 'ml-2'}`}>
                      {message.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="flex items-end space-x-2">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                    <i className="ri-robot-2-line text-white text-sm"></i>
                  </div>
                  <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Options - Sempre visível */}
          <div className="px-4 py-3 border-t border-gray-200 bg-white">
            {/* Resgates Guiados - DESTAQUE NO TOPO */}
            <div className="mb-3">
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-guide-line text-teal-600 text-sm"></i>
                <div className="text-xs font-bold text-teal-700 uppercase tracking-wide">Resgates Guiados</div>
                <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-[10px] font-bold rounded-full">TOUR VIRTUAL</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {guidedRedemptions.map((redemption, index) => (
                  <button
                    key={index}
                    onClick={() => startTour(redemption.tourType)}
                    className="flex items-center space-x-2 px-3 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg text-xs font-medium transition-all cursor-pointer shadow-md hover:shadow-lg transform hover:scale-105"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${redemption.icon} text-base`}></i>
                    </div>
                    <span className="text-left flex-1">{redemption.text}</span>
                    <i className="ri-cursor-line text-teal-200"></i>
                  </button>
                ))}
              </div>
            </div>

            {/* Perguntas Rápidas */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <i className="ri-question-line text-orange-600 text-sm"></i>
                <div className="text-xs font-bold text-orange-700 uppercase tracking-wide">Perguntas Rápidas</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quickQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickQuestion(question.text)}
                    className="flex items-center space-x-2 px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg text-xs transition-colors cursor-pointer border border-orange-200"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${question.icon} text-base`}></i>
                    </div>
                    <span className="text-left flex-1">{question.text}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Digite sua mensagem..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              />
              <button
                type="submit"
                disabled={!inputMessage.trim()}
                className="w-10 h-10 flex items-center justify-center bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-send-plane-fill text-lg"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Tour Overlay */}
      {tour.active && renderTourOverlay()}

      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </>
  );
}
